
import RcDomainObject from "../../../common/RcDomainObject.js";
import Company from "../../company/Company.js";
import CheckListItemOptionMap from "./option/CheckListItemOptionMap.js";
import StringUtils from '@/utils/StringUtils.js'
import VehicleData from "../../vehicle/data/VehicleData.js";

export default class CheckListItem extends RcDomainObject {
  
  static MODEL_NAME = 'CheckListItem';
  
  static Fields = {
      Index: "index",
      Name: "name",
      Type: "type",
      Company: "Company",
      CREATED_DATE: "createdDate",
      OptionMap : CheckListItemOptionMap.MODEL_NAME,
      MultiSelect: "multiSelect",
      WarnOnChange: "warnOnChange",
      WarnChangeThreshold: "warnChangeThreshold",
      Content: "content"
  }
  
  static Values = {
      Types: {
        Text: "text",
        Number: "number",
        List: "list",
        Bool: "bool",
        Info: "info",
        // An hour meter on the asset the checklist is filled in against. A number
        // question in every respect, always metered, and only one to a checklist -
        // a machine has one hour meter, and its reading is kept on the asset
        // itself rather than against the question that asked for it.
        Hours: "hours"
      }
  }
  
  constructor(domain, data) {
    super(domain, data, CheckListItem.MODEL_NAME);
  }

  fields() {
    return CheckListItem.Fields;
  }
  
  find() {
    var result = this.domain.checklists().items().findById(this.id());
    if (result.id() !== this.id()) {
      result = this;
    }
    
    return result;
  }

  populate() {
    var options = this.options();
    options.populate();
    this.withOptions(options);
    return this;
  }
  
  withOptions(options) {
    this.put(CheckListItem.Fields.OptionMap, options);
    return this;
  }
  
  options() {
    var data = this.get(CheckListItem.Fields.OptionMap);
    return new CheckListItemOptionMap(this.domain, data);
  }
  
  index() {
    return this.getInt(CheckListItem.Fields.Index);
  }
  
  withIndex(index) {
    this.put(CheckListItem.Fields.Index, index);
    return this;
  }
  
  name() {
    return this.getString(CheckListItem.Fields.Name);
  }
  
  withName(name) {
    this.put(CheckListItem.Fields.Name, name);
    return this;
  }

  type() {
    return this.getString(CheckListItem.Fields.Type);
  }
  
  withType(type) {
    this.put(CheckListItem.Fields.Type, type);
    return this;
  }
  
  withTypeString() {
    return this.withType(CheckListItem.Values.Types.Text);
  }
  
  withTypeInteger() {
    return this.withType(CheckListItem.Values.Types.Number);
  }
  
  withTypeList() {
    return this.withType(CheckListItem.Values.Types.List);
  }

  withTypeBool() {
    return this.withType(CheckListItem.Values.Types.Bool);
  }

  withTypeInfo() {
    return this.withType(CheckListItem.Values.Types.Info);
  }

  withTypeHours() {
    return this.withType(CheckListItem.Values.Types.Hours);
  }

  isTypeText() {
    return StringUtils.equals(CheckListItem.Values.Types.Text, this.type());
  }
  
  // Hours counts as a number too, the way bool counts as a list: everything that
  // handles a typed-in number treats the two the same, and only the places that
  // care which meter it is ask isTypeHours().
  isTypeNumber() {
    return StringUtils.equals(CheckListItem.Values.Types.Number, this.type())
        || this.isTypeHours();
  }

  // An hour meter on the asset. Only one to a checklist.
  isTypeHours() {
    return StringUtils.equals(CheckListItem.Values.Types.Hours, this.type());
  }

  // What the answer to this question is recorded against on the asset. Every
  // metered question keeps its readings under its own id, so reworking a
  // checklist never mixes two questions' history - except the hour meter, which
  // belongs to the machine rather than to whichever question asked for it.
  readingKey() {
    return this.isTypeHours() ? VehicleData.Values.Keys.Hours : this.id();
  }
  
  isTypeBool() {
    return StringUtils.equals(CheckListItem.Values.Types.Bool, this.type());
  }
  
  isTypeList() {
    return StringUtils.equals(CheckListItem.Values.Types.List, this.type())
        || this.isTypeBool();
  }
  
  // An information block: markdown shown to whoever is filling in the checklist,
  // with nothing to answer and no result recorded against it.
  isTypeInfo() {
    return StringUtils.equals(CheckListItem.Values.Types.Info, this.type());
  }
  
  isAnswerable() {
    return !this.isTypeInfo();
  }
  
  company() {
    var data = this.get(CheckListItem.Fields.Company);
    var company = new Company(this.domain, data);
    return company;
  }
  
  withCompany(company) {
    this.put(CheckListItem.Fields.Company, company);
    return this;
  }
  
  multiSelect() {
    return this.getBoolean(CheckListItem.Fields.MultiSelect)
  }
  
  withMultiSelect(multiSelect) {
    this.put(CheckListItem.Fields.MultiSelect, multiSelect);
    return this;
  }
  
  // An hour meter is always metered - that is what the type is for - so the
  // switch a plain Number question carries is not asked about or stored for one.
  warnOnChange() {
    return this.isTypeHours() || this.getBoolean(CheckListItem.Fields.WarnOnChange);
  }
  
  withWarnOnChange(warnOnChange) {
    this.put(CheckListItem.Fields.WarnOnChange, warnOnChange);
    return this;
  }
  
  warnChangeThreshold() {
    var value = parseFloat(this.get(CheckListItem.Fields.WarnChangeThreshold));
    if (isNaN(value)) {
      return 0;
    }
    return value;
  }
  
  withWarnChangeThreshold(threshold) {
    this.put(CheckListItem.Fields.WarnChangeThreshold, threshold);
    return this;
  }
  
  hasChangeWarning() {
    return this.isTypeNumber()
        && this.warnOnChange()
        && this.warnChangeThreshold() > 0;
  }
  
  content() {
    return this.getString(CheckListItem.Fields.Content);
  }
  
  withContent(content) {
    this.put(CheckListItem.Fields.Content, content);
    return this;
  }
  
  hasContent() {
    return !StringUtils.isEmpty(this.content());
  }
  
  // A one-line plain-text stand-in for an information block, for the places that
  // list items by name and have no room to render the markdown itself.
  contentSummary() {
    var lines = this.content().split("\n");
    for (var index = 0; index < lines.length; index++) {
      var line = lines[index]
        .replace(/^\s*[#>\-*+]+\s*/, "")
        .replace(/[*_`]/g, "")
        .trim();
      if (line.length > 0) {
        return line.length > 80 ? line.substring(0, 80) + "..." : line;
      }
    }
    return "";
  }
  
  displayLabel() {
    if (this.isTypeInfo()) {
      var summary = this.contentSummary();
      return summary.length > 0 ? summary : "Information";
    }
    return this.name();
  }
  copy() {
    var data = this._copy();
    return new CheckListItem(this.domain, data);
  }
}